import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels, annotationPlugin);

const formatValue = (value) => {
    if (value !== undefined && value !== null) {
      return value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    }
    return '';
  };

// Palette catégorielle validée (colorblind-safe, ordre fixe, delta-E >= 12)
const CATEGORICAL_PALETTE = [
    '#2a78d6', // bleu
    '#1baf7a', // aqua
    '#eda100', // jaune
    '#008300', // vert
    '#4a3aa7', // violet
    '#e34948', // rouge
    '#e87ba4', // magenta
    '#eb6834', // orange
];
const OTHER_COLOR = '#94a3b8'; // gris neutre pour la catégorie "Autres"
const MAX_SLICES = CATEGORICAL_PALETTE.length;

const withHexAlpha = (hex, alpha) => {
    const a = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `${hex}${a}`;
};

// Regroupe les catégories excédentaires (au-delà de MAX_SLICES) dans une entrée "Autres"
const foldIntoOthers = (labels, values) => {
    if (labels.length <= MAX_SLICES) return { labels, values };

    const paired = labels.map((label, i) => ({ label, value: values[i] }));
    paired.sort((a, b) => b.value - a.value);

    const kept = paired.slice(0, MAX_SLICES - 1);
    const rest = paired.slice(MAX_SLICES - 1);
    const othersTotal = rest.reduce((sum, item) => sum + item.value, 0);

    return {
        labels: [...kept.map(item => item.label), 'Autres'],
        values: [...kept.map(item => item.value), othersTotal],
    };
};

const generateLegendData = (chartData, total) => {
    const dataset = chartData.datasets[0];
    return chartData.labels
        .map((label, i) => {
            const value = dataset.data[i];
            const percentage = ((value / total) * 100).toFixed(1);
            if (percentage <= 0.01) return null; // Exclude zero values
            return {
                label: `${label} (${percentage}%)`,
                rawLabel: label,
                backgroundColor: dataset.backgroundColor[i],
                borderColor: dataset.borderColor[i],
            };
        })
        .filter(item => item !== null); // Remove null entries
};

const WaterUsageChart = ({ data, unit, totalInitConsumption = null, onSegmentClick, selectedLabel = null }) => {
    const { labels, values } = foldIntoOthers(Object.keys(data), Object.values(data));
    const total = Math.round(values.reduce((acc, value) => acc + value, 0));

    const backgroundColor = labels.map((label, i) =>
        label === 'Autres' ? withHexAlpha(OTHER_COLOR, 0.7) : withHexAlpha(CATEGORICAL_PALETTE[i % MAX_SLICES], 0.85)
    );
    const borderColor = labels.map((label, i) =>
        label === 'Autres' ? OTHER_COLOR : CATEGORICAL_PALETTE[i % MAX_SLICES]
    );
    const offset = labels.map((label) => (label === selectedLabel ? 14 : 0));

    const chartData = {
        labels: labels,
        datasets: [
            {
                label: 'Consommation d\'eau par usage',
                data: values,
                backgroundColor,
                borderColor,
                borderWidth: 2,
                offset,
                hoverOffset: 6,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements, chart) => {
            if (!onSegmentClick || !elements.length) return;
            const index = elements[0].index;
            onSegmentClick(chart.data.labels[index]);
        },
        onHover: (event, elements) => {
            if (event.native?.target) {
                event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
            }
        },
        plugins: {
            legend: {
                display: false, // This will show the legend
                position: 'right', // Position the legend to the right
                labels: {
                    usePointStyle: true,
                    generateLabels: (chart) => {
                        const dataset = chart.data.datasets[0];
                        return chart.data.labels.map((label, i) => {
                            const value = dataset.data[i];
                            const percentage = ((value / total) * 100).toFixed(1);
                            return {
                                text: `${label} (${percentage}%)`, // Ajoute le pourcentage
                                fillStyle: dataset.backgroundColor[i],
                                strokeStyle: dataset.borderColor[i],
                                lineWidth: 1,
                                hidden: false,
                                index: i,
                            };
                        });
                    },
                },
            },
            tooltip: {
                backgroundColor: '#181c22',
                padding: 10,
                cornerRadius: 8,
                titleFont: { size: 13, weight: '600' },
                bodyFont: { size: 13 },
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = context.raw;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${formatValue(Math.round(value))} ${unit} (${percentage}%)`;
                    },
                },
            },
            datalabels: { display: false },
            annotation: {
                annotations: {
                    totalLabel: {
                        type: 'label',
                        xValue: 0.5,
                        yValue: 0.5,
                        content: [`${formatValue(total)} ${unit}`],
                        font: {
                            size: 26,
                            weight: 'bold',
                            family: "'Inter', sans-serif",
                        },
                        color: '#181c22',
                    },
                },
            }
        },
        cutout: '72%',
        animation: {
            animateRotate: true,
            duration: 500,
        },
    };

    if (totalInitConsumption !== null) {
        options.circumference = 360 * total / totalInitConsumption;
    }

    return (
        <div className="water-usage-chart-container">
            <div className="chart-wrapper">
                <Doughnut data={chartData} options={options} />
            </div>
        </div>
    );
};

export { generateLegendData, CATEGORICAL_PALETTE, foldIntoOthers, OTHER_COLOR, withHexAlpha };
export default WaterUsageChart;
